/*eslint-disable*/

import { useMemo } from 'react';
import useSWR from 'swr';
// utils
import { fetcher, patch, put, post, endpoints } from 'src/utils/api-client';

export async function toggleLocationEvent(corporationId, locationId, eventId) {
  return patch(endpoints.corporation.locations.toggleEvent(corporationId, locationId, eventId));
}

// Discharge-driven geofence relocation suggestions (admin/superuser only).
export function useListGeofenceSuggestions(corporationId, enabled) {
  const url = enabled ? endpoints.corporation.geofenceSuggestions(corporationId) : null;
  const { data, isLoading, error, mutate } = useSWR(url, fetcher);

  return useMemo(
    () => ({
      suggestions: data?.suggestions || [],
      suggestionsCount: data?.count || 0,
      suggestionsLoading: isLoading,
      suggestionsError: error,
      suggestionsRefetch: mutate,
    }),
    [data, isLoading, error, mutate]
  );
}

export async function relocateGeofence(corporationId, locationId, clusterIndex = 0) {
  return post(
    `${endpoints.corporation.locations.relocateGeofence(corporationId, locationId)}?cluster_index=${clusterIndex}`
  );
}

export async function updateLocationFence(corporationId, locationId, coordinates, geofenceMode) {
  return put(endpoints.corporation.updateLocation(corporationId, locationId), {
    coordinates,
    geofence_mode: geofenceMode,
  });
}

export async function setLocationGeofenceMode(corporationId, locationId, mode, rebuild = false) {
  return patch(
    `${endpoints.corporation.locations.geofenceMode(corporationId, locationId)}?mode=${mode}&rebuild=${rebuild}`
  );
}

// ----------------------------------------------------------------------

export function useListCorporations() {
  const URL = endpoints.corporation.list;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      corporations: data || [],
      corporationsLoading: isLoading,
      corporationsError: error,
      corporationsValidating: isValidating,
      corporationsEmpty: !isLoading && !data.length,
      corporationsRefetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetCorporation(id) {
  const shouldFetch = Boolean(id);
  const key = shouldFetch
    ? endpoints.corporation.details(id)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      corporation: data || {},
      corporationLoading: isLoading,
      corporationError: error,
      corporationValidating: isValidating,
      refetchCorporation: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useListCorporationVehicles(corporationId, enabled) {
  const shouldFetch = Boolean(corporationId) && enabled;
  const key = shouldFetch
    ? endpoints.corporation.vehicles(corporationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      vehicles: data || [],
      vehiclesLoading: isLoading,
      vehiclesError: error,
      vehiclesValidating: isValidating,
      refetchVehicles: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useListCorporationDevices(corporationId, enabled) {
  const shouldFetch = Boolean(corporationId) && enabled;
  const key = shouldFetch
    ? endpoints.corporation.devices(corporationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      devices: data || [],
      devicesLoading: isLoading,
      devicesError: error,
      devicesValidating: isValidating,
      refetchDevices: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useListCorporationResources(corporationId, enabled) {
  const shouldFetch = Boolean(corporationId) && enabled;
  const key = shouldFetch
    ? endpoints.corporation.resources(corporationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      resources: data || [],
      resourcesLoading: isLoading,
      resourcesError: error,
      resourcesValidating: isValidating,
      resourcesRefetch: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useListCorporationLocations(corporationId, enabled) {
  const shouldFetch = Boolean(corporationId) && enabled;
  const key = shouldFetch
    ? endpoints.corporation.locations.list(corporationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      locations: data || [],
      locationsLoading: isLoading,
      locationsError: error,
      locationsValidating: isValidating,
      locationsRefetch: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useGetCorporationLocation(corporationId, locationId, enabled) {
  const shouldFetch = Boolean(corporationId) && locationId && enabled;
  const key = shouldFetch
    ? endpoints.corporation.locations.details(corporationId, locationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      location: data || {},
      locationLoading: isLoading,
      locationError: error,
      locationValidating: isValidating,
      locationRefetch: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useListLocationUnloadEvents(corporationId, locationId, enabled) {
  const shouldFetch = Boolean(corporationId) && locationId && enabled;
  const key = shouldFetch
    ? endpoints.corporation.locations.unloadEvents(corporationId, locationId)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      events: data || [],
      eventsLoading: isLoading,
      eventsError: error,
      eventsValidating: isValidating,
      eventsRefetch: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}
